import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { FormErrorSummary } from '@/components/form/FormErrorSummary'
import { FormInput } from '@/components/form/FormInput'
import { Button } from '@/components/ui/Button'
import { Modal } from '@/components/ui/Modal'
import { useConfirmPassword } from '@/features/auth/hooks/useAuth'
import { getErrorMessage, getFieldErrors } from '@/utils/errors'

const schema = z.object({ password: z.string().min(1, 'Password is required') })
type FormValues = z.infer<typeof schema>

interface ConfirmPasswordModalProps {
  show: boolean
  onClose: () => void
  onConfirmed: () => void
}

/** Step-up re-authentication modal, paired with usePasswordConfirmGate. */
export function ConfirmPasswordModal({ show, onClose, onConfirmed }: ConfirmPasswordModalProps) {
  const confirmPassword = useConfirmPassword()
  const [apiError, setApiError] = useState<unknown>(null)
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { password: '' },
  })

  const onSubmit = handleSubmit((values) => {
    setApiError(null)
    confirmPassword.mutate(values, {
      onSuccess: () => {
        reset()
        onConfirmed()
      },
      onError: (error) => setApiError(error),
    })
  })

  return (
    <Modal
      show={show}
      onClose={() => {
        reset()
        onClose()
      }}
      title="Confirm Your Password"
    >
      <p className="text-muted fs-13">This action requires you to re-enter your password.</p>
      <FormErrorSummary
        errors={errors}
        serverErrors={getFieldErrors(apiError)}
        generalMessage={apiError ? getErrorMessage(apiError) : null}
      />
      <form onSubmit={(e) => void onSubmit(e)} noValidate>
        <FormInput control={control} name="password" label="Password" type="password" required />
        <Button type="submit" className="w-100" loading={confirmPassword.isPending}>
          Confirm
        </Button>
      </form>
    </Modal>
  )
}
